import { CartIconProps, SimpleHeader } from '@smuchile/pkg-unimarc-components'
import { getGlobalStyle } from '@smuchile/pkg-unimarc-components/helpers'
import {
  TagLoginRef,
  checkUserLoginFlow,
  datalayerSimpleEvent,
  tagLoginView,
  useCheckUserData,
  useOrderForm,
  useQueryClient,
  useSession,
  useUserWithMembership
} from '@smuchile/pkg-unimarc-hooks'
import { LoginModalBrowse } from 'components/LoginModalBrowse'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

interface ClubUnimarcHeaderProps {
  backToURL?: string
  logoUrl?: string
  isMobile: boolean
  showBackArrow: boolean
  showBackArrowMobile?: boolean
  title?: string
}

export const ClubUnimarcHeader = ({
  backToURL = '/',
  logoUrl = '/',
  isMobile,
  showBackArrow,
  showBackArrowMobile = false,
  title = 'Club Unimarc'
}: ClubUnimarcHeaderProps) => {
  const router = useRouter()
  const orderForm = useOrderForm()
  const { isLoggedIn } = useSession()
  const { data: dataMembership, isLoading: isLoadingMembership } =
    useUserWithMembership()
  const [tagLoginRef, setTagLoginRef] = useState<TagLoginRef>(undefined)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [isUserMissingData, setIsUserMissingData] = useState(false)
  const [loginModalStep, setLoginModalStep] = useState<string>(undefined)
  const [shouldCheckUser, setShouldCheckUser] = useState(false)
  const [cartQuantity, setCartQuantity] = useState(0)
  const [isCartQuantityLoading, setIsCartQuantityLoading] = useState(true)

  /**
   * Check if user is still missing data after closing login.
   */
  useCheckUserData(shouldCheckUser, isUserMissingData, setIsUserMissingData)
  const queryClient = useQueryClient()

  const handleGoHomePage = () => {
    router.push(logoUrl)
  }

  const handleGoBack = () => {
    router.push(backToURL)
  }

  const handleOpenLoginModal = (open, ref?: TagLoginRef) => {
    setTagLoginRef(ref)
    setLoginModalOpen(open)
    if (ref && open) tagLoginView(ref)
  }

  const handleOnClickCart = () => {
    datalayerSimpleEvent({
      event: 'interacciones_header',
      eventAction: 'clic',
      eventCategory: 'header',
      eventLabel: 'carrito'
    })
    const validation = checkUserLoginFlow(
      isLoggedIn,
      orderForm?.data?.data?.selectedAddresses,
      false
    )

    if (validation) {
      router.push(process.env.NEXT_PUBLIC_CARTURL)
    } else {
      handleOpenLoginModal(true, 'icono carrito')
    }
  }

  const handleCloseLoginModal = async () => {
    handleOpenLoginModal(false)
    setShouldCheckUser(true)
  }

  const cartQuantityFromQuery =
    queryClient.getQueryData('/cart')?.['products']?.[0]?.totals

  const cartProps: CartIconProps = {
    backgroundQuantity: getGlobalStyle('--color-primary-red'),
    colorIcon: getGlobalStyle('--color-neutral-black'),
    colorPrice: getGlobalStyle('--color-neutral-black'),
    colorQuantity: getGlobalStyle('--color-neutral-white'),
    customSizesIcon: 24,
    customSizesQuantityContainer: '18px',
    isMobile: isMobile,
    quantity: cartQuantity,
    quantityLoading: isCartQuantityLoading,
    site: 'unimarc',
    onClickCart: handleOnClickCart
  }

  useEffect(() => {
    if (dataMembership?.code === 'USR-23') {
      setIsUserMissingData(true)
    }
  }, [dataMembership, isLoadingMembership])

  useEffect(() => {
    setLoginModalOpen(isUserMissingData)
    setLoginModalStep(isUserMissingData ? 'missingUserData' : 'login')
  }, [isUserMissingData])

  useEffect(() => {
    setCartQuantity(cartQuantityFromQuery || 0)
    setIsCartQuantityLoading(false)
  }, [cartQuantityFromQuery])

  return (
    <>
      <LoginModalBrowse
        defaultFormStep={loginModalStep}
        isOpen={loginModalOpen}
        onClose={handleCloseLoginModal}
        router={router}
        tagLoginRef={tagLoginRef}
      />
      <SimpleHeader
        cartProps={cartProps}
        handleGoBack={handleGoBack}
        handleGoHomePage={handleGoHomePage}
        isMobile={isMobile}
        showBackArrow={showBackArrow}
        showBackArrowMobile={showBackArrowMobile}
        showCartIcon={isLoggedIn}
        title={title}
      />
    </>
  )
}
